/**
 * DOM helpers.
 *
 * Small on purpose. The prototype builds screens from template literals and uses
 * event delegation throughout — one listener on the rail, one on the tree, one on
 * the feed — which is what keeps it fast on a tree with hundreds of nodes. These
 * helpers keep that pattern honest rather than replacing it.
 */

/**
 * Escape text for interpolation into a template literal.
 *
 * Everything a relative typed goes through this: names, blog posts, recipe
 * notes, memorial tributes, and anything Keeper drafted from them. A family blog
 * is user-generated content posted by eighty-seven people, some of whom will
 * paste HTML from an email without meaning to.
 */
export function esc(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Escape a value for use inside an HTML attribute. */
export function attr(value) {
  return esc(value);
}

/** Build an element from an HTML string. */
export function fromHTML(html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  return template.content.firstElementChild;
}

/** Replace a container's contents with an HTML string. */
export function render(container, html) {
  container.innerHTML = html;
  nameTheSwitches(container);
  return container;
}

/**
 * Give every switch the name a screen reader needs.
 *
 * A `.tr` toggle is a bare button with no text in it — the design puts its label in the `<div>`
 * beside it, inside the same `.sw` row. That reads correctly and announces as nothing at all:
 * "button, switch" with no indication of what it switches. Measured on the calendar, where
 * three of them sit in a row.
 *
 * Done here rather than at each of the sixteen places a switch is written, because the point is
 * that it cannot be forgotten at the seventeenth. The label is read from the markup that is
 * already there, so nothing has to be kept in step by hand.
 *
 * `aria-checked` comes along with it: the `on` class is how the design carries state, and a
 * switch that announces its name but not whether it is on is only half named. Screens toggle
 * that class directly, so the attribute is refreshed on the next render and by `syncSwitch`
 * for the presses in between.
 */
export function nameTheSwitches(container) {
  for (const toggle of container.querySelectorAll('.sw .tr')) {
    if (!toggle.getAttribute('aria-label')) {
      // The row's text, minus the toggle's own (which is empty). `.d` holds the hint under
      // the label; the label alone is the name, and the hint would make it a paragraph.
      const row = toggle.closest('.sw');
      const label = row?.querySelector('div');
      const hint = label?.querySelector('.d');
      const text = (label?.textContent ?? '').replace(hint?.textContent ?? '', '').trim();
      if (text) toggle.setAttribute('aria-label', text);
    }
    toggle.setAttribute('role', 'switch');
    toggle.setAttribute('aria-checked', String(toggle.classList.contains('on')));
  }
}

/**
 * Keep a switch's announced state in step after it is pressed.
 *
 * The screens toggle `on` directly, which is fine for the eye and silent for a screen reader.
 * Call this after flipping one, or let the next render do it.
 */
export function syncSwitch(toggle) {
  if (toggle) toggle.setAttribute('aria-checked', String(toggle.classList.contains('on')));
}

/**
 * Delegate an event from a container to matching descendants.
 *
 * Returns an unsubscribe function so a screen can clean up when it unmounts —
 * without it, switching screens 30 times leaves 30 live listeners on a
 * long-running kitchen-tablet session.
 */
export function delegate(container, eventName, selector, handler) {
  const listener = (event) => {
    const target = event.target.closest(selector);
    if (target && container.contains(target)) handler(event, target);
  };
  container.addEventListener(eventName, listener);
  return () => container.removeEventListener(eventName, listener);
}

/** Show a field-level validation error next to an input. */
export function showFieldErrors(form, fields) {
  form.querySelectorAll('[data-field-error]').forEach((node) => node.remove());
  form.querySelectorAll('[aria-invalid]').forEach((node) => node.removeAttribute('aria-invalid'));

  if (!fields) return;

  for (const [name, messages] of Object.entries(fields)) {
    const input = form.querySelector(`[name="${name}"]`);
    if (!input) continue;

    input.setAttribute('aria-invalid', 'true');

    const message = Array.isArray(messages) ? messages[0] : messages;
    const note = document.createElement('p');
    note.dataset.fieldError = name;
    note.className = 'field-error';
    note.textContent = message;
    input.insertAdjacentElement('afterend', note);
  }
}

/**
 * Read a form as a plain object, trimming strings.
 *
 * Trimming matters more than usual here: relatives paste phone numbers and email
 * addresses out of contact cards, and a trailing space would create a second
 * account for someone already on the tree.
 */
export function formData(form) {
  const data = {};
  for (const [key, value] of new FormData(form).entries()) {
    data[key] = typeof value === 'string' ? value.trim() : value;
  }
  return data;
}

/** Toggle a button into a pending state, returning a reset function. */
export function pending(button, label = 'Working…') {
  const original = button.textContent;
  button.disabled = true;
  button.dataset.pending = 'true';
  button.textContent = label;

  return () => {
    button.disabled = false;
    delete button.dataset.pending;
    button.textContent = original;
  };
}
